// Compare 2-4 areas: their history of one metric on one chart, and a table of key numbers side by side.
import { api, type GeoInfo, type Level, type Metric, type Scores, type Series } from "./api.ts";
import { fmt, LEVEL_LABEL, metricPeriod } from "../shared/format.ts";
import { el, nodes } from "./dom.ts";
import { metricSelect, placeSearch } from "./widgets.ts";

import { MAX_COMPARE } from "./hash.ts";
export { MAX_COMPARE };
/** Line colors, one per area (distinct in light and dark; the U.S. reference line is dashed gray). */
const COMPARE_COLORS = ["#2563eb", "#e8455a", "#2e9e5b", "#a855f7"];
/** The key-numbers table, top to bottom. */
const KEY = [
  "home_value", "home_value_yoy", "forecast_pct", "decline_risk", "overvalued_pct", "buyer_leverage", "sale_to_list",
  "price_cut_pct", "dom", "inventory_vs_2019", "months_supply", "payment_pct_income", "salary_to_afford", "rent",
  "cap_rate", "rental_cash_flow", "rental_coc", "rental_dscr", "insurance_premium", "hazard_loss", "median_income", "population_growth",
];

export interface CompareDeps {
  metrics: Map<string, Metric>;
  getIds(): string[];
  setIds(ids: string[]): void;
  getMetric(): Metric;
  setMetric(id: string): void;
  getFreq(): "monthly" | "yearly";
  onOpenArea(geoId: string): void;
  onClose(): void;
}

/** Which areas did best on a row: the highest or lowest value, by the metric's direction (neutral: none). */
export function bestIndex(m: Pick<Metric, "higher">, values: Array<number | null | undefined>): number | null {
  if (m.higher === "neutral") return null;
  let best: number | null = null;
  values.forEach((v, i) => {
    if (v == null || !Number.isFinite(v)) return;
    if (best == null || (m.higher === "good" ? v > values[best]! : v < values[best]!)) best = i;
  });
  // a tie or a single value says nothing
  const n = values.filter((v) => v != null && Number.isFinite(v)).length;
  return n >= 2 && best != null && values.filter((v) => v === values[best!]).length === 1 ? best : null;
}

export class CompareView {
  private seq = 0;
  private destroy?: () => void;
  private cache = new Map<string, Promise<[GeoInfo | null, Scores | null]>>();

  constructor(private readonly root: HTMLElement, private readonly d: CompareDeps) {}

  get open() {
    return !this.root.hidden;
  }

  show() {
    this.root.hidden = false;
    void this.render();
  }

  hide() {
    this.root.hidden = true;
    this.destroy?.();
    this.destroy = undefined;
  }

  refresh() {
    if (this.open) void this.render();
  }

  private area(id: string) {
    let p = this.cache.get(id);
    if (!p) {
      p = Promise.all([api.geo(id).catch(() => null), api.scores(id).catch(() => null)]);
      this.cache.set(id, p);
      // a failed lookup is asked again on the next render, not remembered
      void p.then(([g, s]) => { if (!g || !s) this.cache.delete(id); });
    }
    return p;
  }

  private async render() {
    const seq = ++this.seq;
    const ids = this.d.getIds();
    const m = this.d.getMetric();
    const freq = this.d.getFreq();
    const [areas, us, series, usSeries, chart] = await Promise.all([
      Promise.all(ids.map((id) => this.area(id))),
      this.area("us"),
      Promise.all(ids.map((id) => api.series(id, m.id, freq).catch(() => null))),
      api.series("us", m.id, freq).catch(() => null),
      import("./chart.ts"), // uPlot: loaded the first time a chart is drawn (D5)
    ]);
    if (seq !== this.seq) return;
    const { compareChart } = chart;
    const geos = areas.map(([g]) => g);
    const name = (g: GeoInfo | null, id: string) => (g ? (g.level === "zip" ? `ZIP ${g.code}` : g.name) : id);

    // ---- header: areas as removable chips + add ----
    const { input: search, list: results } = placeSearch({ placeholder: "Add an area: ZIP, city, county, metro…", label: "Add an area", listClass: "compare-search",
      keep: (r) => !ids.includes(r.geo_id), onPick: (r) => this.d.setIds([...ids, r.geo_id].slice(-MAX_COMPARE)) });
    const chips = el("div", { class: "compare-chips" },
      ...ids.map((id, i) => el("span", { class: "compare-chip", style: `--c:${COMPARE_COLORS[i]}` },
        el("button", { class: "link", title: "Open this area", onclick: () => this.d.onOpenArea(id) }, name(geos[i]!, id)),
        el("span", { class: "tag" }, LEVEL_LABEL[id === "us" ? "us" : id.split(":")[0]!]!),
        el("button", { class: "btn icon", "aria-label": `Remove ${name(geos[i]!, id)}`, onclick: () => this.d.setIds(ids.filter((x) => x !== id)) }, "✕"))),
      ids.length < MAX_COMPARE ? el("div", { class: "search compare-add" }, search, results) : null);

    // ---- chart ----
    const select = metricSelect({ metrics: this.d.metrics.values(), value: m.id, onChange: (id) => this.d.setMetric(id),
      include: (x) => !x.personal || Object.keys(x.available).length > 0 });
    const host = el("div", { class: "chart-host compare-chart" });
    const missing = ids.filter((id, i) => !series[i]?.points.some((p) => p.value != null)).map((id) => name(geos[ids.indexOf(id)]!, id));

    // ---- key numbers ----
    const cols = [...ids.map((id, i) => ({ id, g: geos[i]!, s: areas[i]![1] })), { id: "us", g: us[0], s: us[1] as Scores | null }];
    const rowFor = (label: string, mm: Metric, vals: Array<number | null | undefined>, onclick?: () => void) => {
      const best = bestIndex(mm, vals.slice(0, ids.length));
      return el("tr", { class: onclick ? "clickable" : "", onclick, title: onclick ? `Chart ${mm.label}` : "" },
        el("th", { scope: "row" }, label, mm.id === m.id ? el("span", { class: "muted" }, " · charted") : null),
        ...vals.map((v, i) => el("td", { class: `num ${i === best ? "best" : ""} ${i === ids.length ? "muted" : ""}` }, fmt(mm, v ?? null))));
    };
    const pct: Metric = { ...this.d.metrics.get("home_value_yoy")!, id: "_p_rise", label: "Chance of a rise", unit: "pct", higher: "good" };
    const rows = KEY.map((id) => this.d.metrics.get(id)).filter((mm): mm is Metric => !!mm && cols.some((c) => c.g?.latest[mm.id] != null))
      .map((mm) => rowFor(mm.label, mm, cols.map((c) => c.g?.latest[mm.id]?.value), () => this.d.setMetric(mm.id)));
    const riseVals = cols.map((c) => c.s?.forecast.likely?.p_rise ?? null);
    if (riseVals.some((v) => v != null)) rows.splice(3, 0, rowFor("Chance of a rise (1-year)", pct, riseVals));

    this.root.replaceChildren(...nodes(
      el("header", { class: "table-head" },
        el("div", {},
          el("h2", {}, "Compare areas"),
          el("div", { class: "muted small" }, `Up to ${MAX_COMPARE} areas of any level, with the U.S. for reference. Latest values; the best in each row is marked.`)),
        el("div", { class: "panel-actions" },
          el("button", { class: "btn icon", "aria-label": "Close compare", onclick: () => this.d.onClose() }, "✕"))),
      el("div", { class: "compare-body" },
        chips,
        ids.length === 0 ? el("p", { class: "muted" }, "Add areas above, or use “Compare” in any area's panel.") : null,
        ids.length ? el("section", { class: "card compare-card" },
          el("div", { class: "compare-chart-head" }, el("h3", {}, m.label), select),
          missing.length ? el("div", { class: "muted small" }, `No ${m.label} history for ${missing.join(", ")}.`) : null,
          host) : null,
        ids.length ? el("section", { class: "card compare-card" },
          el("div", { class: "table-scroll" }, el("table", { class: "rank compare-table" },
            el("thead", {}, el("tr", {}, el("th", {}, ""),
              ...cols.map((c, i) => el("th", { class: "num", style: i < ids.length ? `color:${COMPARE_COLORS[i]}` : "" }, c.id === "us" ? "U.S." : name(c.g, c.id))))),
            el("tbody", {}, ...rows)))) : null)));

    this.destroy?.();
    this.destroy = ids.length ? compareChart(host, m, ids.map((id, i) => ({ name: name(geos[i]!, id), color: COMPARE_COLORS[i]!, s: series[i] ?? null })),
      usSeries) : undefined;
  }
}
